import './style.scss';
import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { Currency } from 'types';
import { ShortlistedSummary } from 'types/campaign/applications';
import { formatAmount, formatNumber } from 'utils/helpers';

const TIER_ORDER = ['nano', 'micro', 'macro', 'mega'];

const creatorWord = (count: number) => (count === 1 ? 'creator' : 'creators');

const formatTierLabel = (tierKey: string, count: number): string => {
    const normalized = tierKey.replace(/_/g, ' ').toLowerCase().trim();
    const word = creatorWord(count);

    if (/\b(creator|creators)\b/.test(normalized)) {
        return normalized.replace(/\bcreators?\b/g, word);
    }

    return `${normalized} ${word}`;
};

const formatRoiMetric = (value: number): string => {
    if (!value) return '0';
    const formatted = formatNumber(value);
    return formatted === '-' ? '0' : formatted;
};

const sortTierEntries = (creatorTiers: ShortlistedSummary['creator_tiers']) =>
    Object.entries(creatorTiers).sort(([a], [b]) => {
        const aIndex = TIER_ORDER.indexOf(a.toLowerCase());
        const bIndex = TIER_ORDER.indexOf(b.toLowerCase());
        const aRank = aIndex === -1 ? TIER_ORDER.length : aIndex;
        const bRank = bIndex === -1 ? TIER_ORDER.length : bIndex;
        if (aRank !== bRank) return aRank - bRank;
        return a.localeCompare(b);
    });

export interface ShortlistedSummaryPanelProps {
    summary: ShortlistedSummary | null;
    loading?: boolean;
    currency: Currency;
}

const ShortlistedSummaryPanel: React.FC<ShortlistedSummaryPanelProps> = ({
    summary,
    loading = false,
    currency,
}) => {
    const tierEntries = summary ? sortTierEntries(summary.creator_tiers) : [];
    const totalInfluencers = summary?.application_count ?? 0;
    const totalCost = summary?.cost?.net ?? 0;

    if (loading) {
        return (
            <aside className="shortlisted-summary" aria-label="Shortlisted summary">
                <div className="shortlisted-summary__card shortlisted-summary__card--loading">
                    <Skeleton height={160} className="w-100 d-block rounded mb-3" />
                    <Skeleton height={48} className="w-100 d-block rounded mb-3" />
                    <Skeleton height={36} className="w-100 d-block rounded" />
                </div>
            </aside>
        );
    }

    return (
        <aside className="shortlisted-summary" aria-label="Shortlisted summary">
            <div className="shortlisted-summary__card">
                <div className="shortlisted-summary__content">
                    <div className="shortlisted-summary__breakdown-card">
                        {tierEntries.length > 0 ? (
                            <div className="shortlisted-summary__tier-columns">
                                <div className="shortlisted-summary__tier-labels">
                                    {tierEntries.map(([tierKey, tier]) => (
                                        <p key={tierKey} className="shortlisted-summary__tier-label">
                                            <span className="shortlisted-summary__tier-count">
                                                {String(tier.count).padStart(2, '0')}
                                            </span>
                                            <span>{` - ${formatTierLabel(tierKey, tier.count)}`}</span>
                                        </p>
                                    ))}
                                </div>
                                <div className="shortlisted-summary__tier-costs">
                                    {tierEntries.map(([tierKey, tier]) => (
                                        <p key={tierKey} className="shortlisted-summary__tier-cost">
                                            {formatAmount(tier.cost, currency?.code, currency?.symbol, currency?.locale)}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <p className="shortlisted-summary__empty-tiers text-muted mb-0">
                                No shortlisted creators yet
                            </p>
                        )}
                        {tierEntries.length > 0 && <div className="shortlisted-summary__divider" />}
                        <div className="shortlisted-summary__total-row">
                            <p className="shortlisted-summary__total-label">
                                Total {creatorWord(totalInfluencers)} ({totalInfluencers})
                            </p>
                            <p className="shortlisted-summary__total-cost">{formatAmount(totalCost, currency?.code, currency?.symbol, currency?.locale)}</p>
                        </div>
                    </div>
                    <div className="shortlisted-summary__roi">
                        <p className="shortlisted-summary__roi-title">EXPECTED ROI</p>
                        <div className="shortlisted-summary__roi-grid">
                            <p className="shortlisted-summary__roi-metric">
                                <span>Views: </span>
                                <span className="shortlisted-summary__roi-value">
                                    {formatRoiMetric(summary?.roi?.average_views ?? 0)}
                                </span>
                            </p>
                            <p className="shortlisted-summary__roi-metric">
                                <span>Engagement: </span>
                                <span className="shortlisted-summary__roi-value">
                                    {formatRoiMetric(summary?.roi?.average_engagement ?? 0)}
                                </span>
                            </p>
                            <p className="shortlisted-summary__roi-metric">
                                <span>CPV: </span>
                                <span className="shortlisted-summary__roi-value">
                                    {formatRoiMetric(summary?.roi?.cpv ?? 0)}
                                </span>
                            </p>
                            <p className="shortlisted-summary__roi-metric">
                                <span>CPE: </span>
                                <span className="shortlisted-summary__roi-value">
                                    {formatRoiMetric(summary?.roi?.cpe ?? 0)}
                                </span>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </aside>
    );
};

export default ShortlistedSummaryPanel;
